import React,{useEffect, useState, useRef} from 'react';

import './index.scss';

import ProfileAvatar from "../ProfileAvatar";
import CheckBox from "../CheckBox";
import RadioButton from "../RadioButton";

import { searchIcon } from "../../../utils/icons";
import { getOrganizationImageUrl, getDomainImageUrl, getUserImageUrl, getCustomerImageUrl } from "../../../utils/util";
import SkeletonLoader from "../SkeletonLoader";

// Icon component
const Icon = ({ isOpen }) => {
    return (
        <svg viewBox="0 0 24 24" width="18" height="18" stroke="#222" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" className={isOpen ? 'translate' : ''}>
            <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
    );
};

// CloseIcon component
const CloseIcon = () => {
    return (
        <svg viewBox="0 0 24 24" width="14" height="14" stroke="#00308B" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
    );
};

const DropDown = ({ 
    placeHolder, 
    options, 
    isMulti, 
    isSearchable, 
    onChange, 
    align, 
    dropDownType, 
    resetValue=false, 
    defaultSelectedValue="", 
    disabled=false, 
    imageType="", 
    searchType="internal", 
    updateSearchValue, 
    isSearchLoading=false,
    onScrollEnd,
    pageLoading
}) => {
    // State variables using React hooks
    const [showMenu, setShowMenu] = useState(false); // Controls the visibility of the dropdown menu
    const [selectedValue, setSelectedValue] = useState(isMulti ? [] : null); // Stores the selected value(s)
    const [searchValue, setSearchValue] = useState(""); // Stores the value entered in the search input
    const searchRef = useRef(); // Reference to the search input element
    const inputRef = useRef(); // Reference to the custom select input element
    const dropdownMenuRef = useRef(); // Ref for dropdown menu

    useEffect(() => {
        if(searchType === "internal") {
            setSearchValue("");
        }        
        if (showMenu && searchRef.current) {
            searchRef.current.focus();
        }
    }, [showMenu, searchType]);

    useEffect(() => {
        const handler = (e) => {
            if (inputRef.current && !inputRef.current.contains(e.target)) {
                setShowMenu(false);
            }
        };

        window.addEventListener("click", handler);
        return () => {
            window.removeEventListener("click", handler);
        };
    });

    useEffect(()=>{        
        if(resetValue) {            
            setSelectedValue([])
        }
    },[resetValue])

    useEffect(() => {
        const dropdownMenu = dropdownMenuRef.current; // Store ref in a variable
    
        const handleScroll = () => {
            if (!dropdownMenu) return;
            const { scrollTop, scrollHeight, clientHeight } = dropdownMenu;
            const scrolledPercentage = (scrollTop / (scrollHeight - clientHeight)) * 100;
            
            if (scrolledPercentage >= 95 && onScrollEnd) {
                onScrollEnd(); // Emit event to parent
            }
        };
    
        if (dropdownMenu) {
            dropdownMenu.addEventListener("scroll", handleScroll);
    
            //Check if the dropdown is already scrolled to 98% on first render
            const { scrollTop, scrollHeight, clientHeight } = dropdownMenu;
            const initialScrolledPercentage = (scrollTop / (scrollHeight - clientHeight)) * 100;
    
            if (initialScrolledPercentage >= 98 && onScrollEnd) {
                onScrollEnd(); // Trigger on first load if already scrollable
            }
        }
    
        return () => {
            if (dropdownMenu) {
                dropdownMenu.removeEventListener("scroll", handleScroll);
            }
        };
    }, [onScrollEnd, showMenu]);

    const handleInputClick = (e) => {
        setShowMenu(!showMenu);
    };

    const getDisplay = () => {     
        if (!isMulti && (!selectedValue || selectedValue.length === 0)) {
            return placeHolder;
        }
        if (isMulti && selectedValue?.length > 0) {
            return (
                <div className="dropdown-tags">
                    {
                        selectedValue.map((option, index) => (
                            <div key={`${option.value}-${index}`} className="dropdown-tag-item">
                                {option.label}
                                <span onClick={(e) => onTagRemove(e, option)} className="dropdown-tag-close" >
                                    <CloseIcon />
                                </span>
                            </div>
                        ))
                    }
                </div>
            );
        }
        return selectedValue ? selectedValue.label : "";
    };

    const displaySelectedValue = () => {
        if(selectedValue !== null && resetValue === false) {
            return (
                <>
                    {imageType === "customer" && <ProfileAvatar type="domain" name={selectedValue?.name} imageUrl={getCustomerImageUrl(selectedValue)} imageType={"square"}/>}
                    {imageType === "organization" && <ProfileAvatar type="domain" name={selectedValue?.name} imageUrl={getOrganizationImageUrl(selectedValue)} imageType={"square"}/>}
                    {imageType === "domain" && <ProfileAvatar type="domain" name={selectedValue?.name} imageUrl={getDomainImageUrl(selectedValue)} imageType={"square"}/>}
                </>                
            )
        } else {
            return placeHolder
        }
    }

    const removeOption = (option) => {
        return selectedValue.filter((o) => o.value !== option.value);
    };

    const onTagRemove = (e, option) => {
        e.stopPropagation();
        const newValue = removeOption(option);
        setSelectedValue(newValue);
        onChange(newValue);
    };

    
    const onItemClick = (option) => {
        if (dropDownType === "scanInfo" || dropDownType === "addDomain" || dropDownType === "reportSubdomain") {
            setRadioChange(option.id); // Handles RadioButton clicks
        } else if (dropDownType === "modifyDomain" || dropDownType === "modifyUser" || dropDownType === "modifyCustomer" || dropDownType === "modifyOrganization" || dropDownType === "reportModule") {
            const event = { target: { checked: !isChecked(option), value: option.id } };
            setCheckboxvalue(event); // Handles CheckBox clicks
        } else {
            // Default dropdown behavior
            let newValue;
            if (isMulti) {
                if (selectedValue.findIndex((o) => o.value === option.value) >= 0) {
                    newValue = removeOption(option);
                } else {
                    newValue = [...selectedValue, option];
                }
            } else {
                newValue = option;
                setShowMenu(false);
            }
            setSelectedValue(newValue);
            onChange(newValue);
        }
    };

    const isSelected = (option) => {
        if (isMulti) {
            return selectedValue.filter((o) => o.value === option.value).length > 0;
        }

        if (!selectedValue) {
            return false;
        }

        return selectedValue.value === option.value;
    };

    const onSearch = (e) => {
        setSearchValue(e.target.value);
        updateSearchValue(e.target.value);
    };

    const getOptions = () => {
        if(searchType === "internal") {
            if (!searchValue) {
                return options;
            }
            return options.filter(
                (option) =>
                    option.label.toLowerCase().indexOf(searchValue.toLowerCase()) >= 0
            );
        } else {
            // if (searchValue.trim() !== "") {  //Call updateSearchValue ONLY when user types
                
            // }
            
            return options;
        }        
    };

    const setCheckboxvalue = (e) => {
        // Ensure selectedValue is an array before using it
        const currentSelected = Array.isArray(selectedValue) ? selectedValue : [];
    
        if (e.target.checked) {
            let checkedValue = options.filter(item => item.id === e.target.value);
            setSelectedValue([...currentSelected, ...checkedValue]);
            onChange([...currentSelected, ...checkedValue]);
        } else {
            const updatedList = currentSelected.filter(item => item.id !== e.target.value);
            setSelectedValue(updatedList);
            onChange(updatedList);
        }
    };

    const isChecked = (data) => {
        return selectedValue !== null && selectedValue.some(item => item.id === data.id);
    }

    const setRadioChange = (e) => {
        const checkedValue = options.find(item => item.id === e); // `find` returns the first matching object, not an array
        setSelectedValue(checkedValue); // Set the selected value as an object, not an array
        onChange(checkedValue); // Call onChange to notify parent component if needed
        setShowMenu(false)
    };
    
    const isRadioChecked = (data) => {
        return selectedValue !== null && selectedValue.id === data.id;
    };

    useEffect(()=>{
        if(Array.isArray(defaultSelectedValue)) {
            setSelectedValue(defaultSelectedValue)
        } else if (defaultSelectedValue === "") {
            setSelectedValue(null)
        } else {
            const defaultOption = options.find(
                (option) => option.value === defaultSelectedValue
            );            
            if (defaultOption) {
                setSelectedValue(defaultOption); // Set the default selected value
            }
        }
    },[defaultSelectedValue, options])

    return (
        <>
            <div className={`custom--dropdown-container ${disabled ? "disabled" : ""}`} ref={inputRef}>
                <div onClick={handleInputClick} className="dropdown-input position-relative">
                    {isMulti && <div className={"dropdown-selected-value placeholder"}>{placeHolder}</div>}
                    {(dropDownType === "scanInfo" || dropDownType === "customerList") &&  <div className={`dropdown-selected-value ${!selectedValue || selectedValue.length === 0 ? 'placeholder' : ''}`}>{displaySelectedValue()}</div>}
                    {!isMulti && dropDownType !== "scanInfo" && dropDownType !== "customerList" && <div className={`dropdown-selected-value ${!selectedValue || selectedValue.length === 0 ? 'placeholder' : ''}`}>{getDisplay()}</div>}                
                    <div className="dropdown-tools position-absolute">
                        <div className="dropdown-tool">
                            <Icon isOpen={showMenu} />
                        </div>
                    </div>
                </div>
                {
                    showMenu && (
                        <div className={`w-100 dropdown-menu alignment--${align || 'auto'} ${showMenu ? 'active' : ''}`}>
                            {
                                isSearchable && (
                                    <div className="position-relative">
                                        <div className="search-box position-relative">
                                            <span className="position-absolute">{searchIcon()}</span>
                                            <input className="form-control" onChange={onSearch} value={searchValue} ref={searchRef} placeholder="Search"/>
                                        </div>
                                    </div>                                    
                                )
                            }
                            {
                                isSearchLoading ?
                                    <SkeletonLoader type={"line"} count={2} currentPage={"dropdown"} />
                                :
                                <div className="options-container" ref={dropdownMenuRef}>
                                    {
                                        getOptions().map((option,index) => {                                    
                                            if(dropDownType === "modifyDomain" || dropDownType === "modifyUser" || dropDownType === "modifyCustomer" || dropDownType === "modifyOrganization" || dropDownType === "reportModule"){
                                                return (
                                                    <div key={option.id} className="my-3 d-flex justify-content-between dropdown-item" onClick={() => onItemClick(option)}>
                                                        {dropDownType === "modifyUser" && <ProfileAvatar type="profile" firstName={option.firstName} lastName={option.lastName} secondaryText={option.email} imageUrl={getUserImageUrl(option)} />}
                                                        {dropDownType === "modifyDomain" && <ProfileAvatar type="domain" name={option.name} imageUrl={getDomainImageUrl(option)} />}
                                                        {dropDownType === "modifyCustomer" && <ProfileAvatar type="domain" name={option.name} imageUrl={getCustomerImageUrl(option)} />}
                                                        {dropDownType === "modifyOrganization" && <ProfileAvatar type="domain" name={option.name} imageUrl={getOrganizationImageUrl(option)} />}
                                                        {dropDownType === "reportModule" && option.label}
                                                        <CheckBox checkBoxId={index} checkBoxValue={option.id} handleCheckboxChange={setCheckboxvalue} isChecked={isChecked(option)}/>
                                                    </div>
                                                )
                                            } else if (dropDownType === "scanInfo") {
                                                return (
                                                    <div key={option.id} className={`d-flex justify-content-between dropdown-item scan-info-option ${option.id === selectedValue?.id ? "selected" : ""}`} onClick={() => onItemClick(option)}>
                                                        {imageType === "customer" && <ProfileAvatar type="domain" name={option.name} imageUrl={getCustomerImageUrl(option)} imageType={"square"}/>}
                                                        {imageType === "organization" && <ProfileAvatar type="domain" name={option.name} imageUrl={getOrganizationImageUrl(option)} imageType={"square"}/>}
                                                        {imageType === "domain" && <ProfileAvatar type="domain" name={option.name} imageUrl={getDomainImageUrl(option)} imageType={"square"}/>}
                                                        <RadioButton
                                                            radioId={`radio-${index}`}
                                                            radioValue={option.id}
                                                            onChange={setRadioChange}
                                                            isChecked={isRadioChecked(option)}
                                                        />
                                                    </div>
                                                )
                                            } else if (dropDownType === "addDomain") {
                                                return (
                                                    <div key={option.id} className={`my-1 d-flex justify-content-between dropdown-item scan-info-option ${option.id === selectedValue?.id ? "selected" : ""}`} onClick={() => onItemClick(option)}>
                                                        <ProfileAvatar type="domain" name={option.name} imageUrl={getOrganizationImageUrl(option)} imageType={"round"}/>
                                                        <RadioButton
                                                            radioId={`radio-${index}`}
                                                            radioValue={option.id}
                                                            onChange={setRadioChange}
                                                            isChecked={isRadioChecked(option)}
                                                        />
                                                    </div>
                                                )
                                            } else if(dropDownType === "reportSubdomain") {
                                                return (
                                                    <div key={option.id} className={`my-1 d-flex justify-content-between dropdown-item scan-info-option ${option.id === selectedValue?.id ? "selected" : ""}`} onClick={() => onItemClick(option)}>
                                                        {option.label}
                                                        <RadioButton
                                                            radioId={`radio-${index}`}
                                                            radioValue={option.id}
                                                            onChange={setRadioChange}
                                                            isChecked={isRadioChecked(option)}
                                                        />
                                                    </div>
                                                )
                                            } else {
                                                return (
                                                    <div onClick={() => onItemClick(option)} key={option.value} className={`dropdown-item ${isSelected(option) && "selected"}`} >
                                                        {option.label}
                                                    </div>
                                                )
                                            }
                                        })
                                    }
                                    {
                                        pageLoading && <SkeletonLoader type={"line"} count={10} currentPage={"dropdown"} />
                                    }
                                </div>                                
                            }
                        </div>
                    )
                }
                </div>
            {isMulti && <div className={`mt-4 dropdown-selected-value ${!selectedValue || selectedValue.length === 0 ? 'placeholder' : ''}`}>{getDisplay()}</div>}
        </>
    );
}

export default DropDown;